import { getTicketTaxSettings } from '../../services/ticketTaxService';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * TVA — REPLI de calcul pour les modules portés
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ CE N'EST PAS un moteur de TVA : la borne en a déjà un
 * (`services/ticketTaxService.calculateTicketTax`), et c'est LUI qui fait foi. Ce
 * fichier ne sert QUE de repli quand l'objet ticket n'apporte ni HT ni montant de
 * TVA (aperçus, écran de test, appel sans `taxResult`).
 *
 * Équivalent de `utils/taxDisplayHelper.ts` côté console, à une différence près : le
 * taux de repli vient des RÉGLAGES DE LA BORNE (`ticketTaxSettings.customRate`) et
 * jamais d'un 20 en dur.
 */

export type TaxDisplayModeCompat = 'detailed' | 'simple' | 'compact';

export interface TaxCalculation {
  subtotalHT: number;
  taxRate: number;
  taxAmount: number;
  totalTTC: number;
}

/** Taux de repli = celui des Paramètres TVA de la borne. */
const fallbackTaxRate = (): number => {
  try {
    const rate = Number(getTicketTaxSettings().customRate);
    return rate > 0 ? rate : 20;
  } catch {
    return 20;
  }
};

/**
 * Extrait le HT et la TVA d'un TTC.
 * ⚠️ EXTRACTION, jamais ajout : les prix de la borne sont TTC (le magasin est en
 * `is_tax_included`). Ajouter la TVA gonflerait le total du ticket.
 */
export const calculateTax = (
  totalTTC: number,
  apiTaxRate: number | null | undefined
): TaxCalculation => {
  const taxRate =
    apiTaxRate !== null && apiTaxRate !== undefined && Number.isFinite(Number(apiTaxRate))
      ? Number(apiTaxRate)
      : fallbackTaxRate();

  const ttc = Number(totalTTC) || 0;
  const subtotalHT = taxRate > 0 ? ttc / (1 + taxRate / 100) : ttc;
  const taxAmount = ttc - subtotalHT;

  return { subtotalHT, taxRate, taxAmount, totalTTC: ttc };
};

/**
 * Ce qu'on affiche selon le mode.
 * `'hidden'` (vocabulaire de la borne) est traduit en `'compact'` par les appelants.
 */
export const getTaxDisplayFlags = (
  displayMode: TaxDisplayModeCompat | string | undefined
): { showSubtotalHT: boolean; showTaxRate: boolean; showTaxAmount: boolean } => {
  switch (displayMode || 'detailed') {
    case 'simple':
      return { showSubtotalHT: true, showTaxRate: false, showTaxAmount: false };
    case 'compact':
      return { showSubtotalHT: false, showTaxRate: false, showTaxAmount: false };
    case 'detailed':
    default:
      return { showSubtotalHT: true, showTaxRate: true, showTaxAmount: true };
  }
};
